import React, { ImgHTMLAttributes, useEffect, useMemo, useRef, useState } from 'react';
import { useUnmount, useEffectOnce } from 'react-use';
import { always } from 'ramda';
import SliderComponent, { Settings } from 'react-slick';
import { useIntl } from 'react-intl';

import { Image as ImageType } from '@definitions/artworks.types';
import { renderWhenTrue, renderWhenTrueOtherwise } from '@utils/rendering';
import { useWindowListener } from '@hooks/useWindowListener';
import { preventTabIndex } from '@utils/helpers';
import { FocusTrap } from '@components/common';
import { useBreakpoint } from '@hooks/useBreakpoint';

import CloseIcon from '../../../assets/images/close.svg';
import ArrowLeft from '../../../assets/images/arrow-left.svg';
import ArrowRight from '../../../assets/images/arrow-right.svg';
import LoupePlus from '../../../assets/images/loupe-plus.svg';
import LoupeMinus from '../../../assets/images/loupe-minus.svg';

import { CLOSE_TEST_ID, ZOOM_TEST_ID, IMAGE_CONTAINER_TEST_ID } from './imageDetailView.costants';
import {
  Container,
  ControlsPanel,
  ImageCounter,
  ImageElement,
  ImagesContainer,
  ImageContainer,
  ScrollContainer,
  Button,
} from './imageDetailView.styles';
import { imageDetailViewMessages } from './imageDetailView.messages';

const ICON_SIZE = {
  width: 44,
  height: 44,
};

const OFFSET = {
  VERTICAL: 100,
  HORIZONTAL: 40,
};

export interface ZoomImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  isZoom: boolean;
  src: string;
  currentSlide: number;
}

export const ZoomImage = ({ isZoom, src, alt, currentSlide }: ZoomImageProps) => {
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [imageData, setImageData] = useState({ width: 0, height: 0 });
  const imageRef = useRef<HTMLImageElement>(null);
  const [isActive, setIsActive] = useState(false);

  const setImageSize = (image: { width: number; height: number }) => {
    const { width: imageWidth, height: imageHeight } = image;
    const ratio = imageWidth / imageHeight;
    const { innerWidth, innerHeight } = window;

    if (imageWidth > innerWidth && imageHeight > innerHeight) {
      if (innerHeight < innerWidth) {
        const height = innerHeight - OFFSET.VERTICAL;
        setSize({ width: height * ratio, height });
        return;
      }

      const width = innerWidth - OFFSET.HORIZONTAL;
      setSize({ width, height: width / ratio });
      return;
    }

    if (imageWidth > innerWidth) {
      const width = innerWidth - OFFSET.HORIZONTAL;
      setSize({ width, height: width / ratio });
      return;
    }

    if (imageHeight > innerHeight) {
      const height = innerHeight - OFFSET.VERTICAL;
      setSize({ width: height * ratio, height });
      return;
    }

    setSize({ width: imageWidth, height: imageHeight });
  };

  const setActiveImage = () => {
    const slideElement = imageRef.current?.parentElement?.parentElement?.parentElement;
    const active = slideElement?.classList.contains('slick-active') || false;
    setIsActive(active);
  };

  useWindowListener('resize', () => setImageSize(imageData), { throttle: 200 });
  useEffectOnce(() => {
    const image = new Image();
    image.onload = () => {
      const { width, height } = image;
      setImageData({ width, height });
      setImageSize({ width, height });
      setActiveImage();
    };
    image.src = src;
  });

  useEffect(() => {
    setActiveImage();
  }, [currentSlide]);

  const renderImage = renderWhenTrue(() => (
    <ImageElement
      src={src}
      alt={alt}
      initial={false}
      ref={imageRef}
      transition={{ ease: 'linear', duration: 0.2 }}
      tabIndex={preventTabIndex(!isActive)}
      animate={{
        width: isZoom ? size.width * 2 : size.width,
        height: isZoom ? size.height * 2 : size.height,
      }}
    />
  ));

  return renderImage(!!size.width);
};

export interface ImageDetailViewProps {
  images: ImageType[];
  initialImageIndex: number;
  setCarouselOpen: (prop: boolean) => void;
}

export const ImageDetailView = ({ images, setCarouselOpen, initialImageIndex }: ImageDetailViewProps) => {
  const [isZoom, setIsZoom] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(initialImageIndex);
  const { isMobile } = useBreakpoint();
  const sliderRef = useRef<any>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const intl = useIntl();

  const settings = useMemo(
    () =>
      ({
        arrows: false,
        dots: false,
        infinite: true,
        autoplay: false,
        slidesToShow: 1,
        slidesToScroll: 1,
        initialSlide: initialImageIndex - 1,
        afterChange: (index: number) => {
          setCurrentSlide(index + 1);
        },
      } as Settings),
    [images.length]
  );

  useEffectOnce(() => {
    document.body.style.overflow = 'hidden';
  });

  useUnmount(() => {
    document.body.style.overflow = 'visible';
  });

  const renderLoupe = renderWhenTrueOtherwise(
    always(
      <Button onClick={() => setIsZoom(false)} aria-label={intl.formatMessage(imageDetailViewMessages.zoomOut)}>
        <LoupeMinus {...ICON_SIZE} />
      </Button>
    ),
    always(
      <Button
        onClick={() => setIsZoom(true)}
        data-testid={ZOOM_TEST_ID}
        aria-label={intl.formatMessage(imageDetailViewMessages.zoomIn)}
      >
        <LoupePlus {...ICON_SIZE} />
      </Button>
    )
  );

  const handlePrevSlide = () => sliderRef.current?.slickPrev();
  const handleNextSlide = () => sliderRef.current?.slickNext();

  const handleWheel = () => {
    if (!isZoom) {
      setCarouselOpen(false);
    }
  };

  const focusImage = () => {
    const activeImage = sliderRef.current?.innerSlider?.list
      .getElementsByClassName('slick-active')[0]
      .getElementsByTagName('img')[0];

    if (activeImage) {
      activeImage.focus();
    }
  };

  return (
    <Container onWheel={handleWheel}>
      <FocusTrap onFocus={focusImage} />
      <ControlsPanel isMobile={isMobile}>
        <ImageCounter>
          {currentSlide}/{images.length}
        </ImageCounter>
        <Button
          onClick={handlePrevSlide}
          autoFocus
          ref={buttonRef}
          aria-label={intl.formatMessage(imageDetailViewMessages.previous)}
        >
          <ArrowLeft {...ICON_SIZE} />
        </Button>
        <Button onClick={handleNextSlide} aria-label={intl.formatMessage(imageDetailViewMessages.next)}>
          <ArrowRight {...ICON_SIZE} />
        </Button>
        {renderLoupe(isZoom)}
        <Button
          onClick={() => setCarouselOpen(false)}
          data-testid={CLOSE_TEST_ID}
          aria-label={intl.formatMessage(imageDetailViewMessages.close)}
        >
          <CloseIcon {...ICON_SIZE} />
        </Button>
      </ControlsPanel>
      <ScrollContainer>
        <ImagesContainer
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ ease: 'linear', duration: 0.2 }}
          isZoom={isZoom}
          onClick={() => setIsZoom((prev) => !prev)}
          data-testid={IMAGE_CONTAINER_TEST_ID}
        >
          <SliderComponent {...settings} ref={sliderRef} swipe={!isZoom} accessibility={!isZoom}>
            {images.map(({ urlZoom, description }, index) => (
              <ImageContainer key={index}>
                <ZoomImage
                  src={urlZoom}
                  isZoom={isZoom && currentSlide === index + 1}
                  alt={description}
                  currentSlide={currentSlide}
                />
              </ImageContainer>
            ))}
          </SliderComponent>
        </ImagesContainer>
      </ScrollContainer>
      <FocusTrap onFocus={() => buttonRef.current?.focus()} />
    </Container>
  );
};
